/* mg-upsell-track.js
 *
 * One funnel, three surfaces. The store now asks a shopper to add an accessory
 * in three different places, and the only question worth asking about any of
 * them is which one earns its space. That comparison is only fair if all three
 * are measured the same way, by the same code, so this file exists rather than
 * three sets of mgTrack calls that drifted apart within a month.
 *
 *   pdp_addons   the ADD-ONS carousel on a product page
 *   cart_addons  the cart drawer's "Explore add-ons" row
 *   lastchance   the Check Out Exclusives modal
 *
 * Funnel per surface: impression -> click -> add.
 *
 * WHY IMPRESSIONS ARE OBSERVED RATHER THAN COUNTED ON RENDER
 * Two of these surfaces are horizontal carousels. Counting every rendered card
 * would credit a surface for cards nobody ever scrolled to, and would flatter
 * the modal worst of all — its cards are on screen the moment it opens. So an
 * impression means "at least half the card was actually visible for half a
 * second", which is a claim that means the same thing on all three.
 *
 * WHAT REACHES THE SERVER
 * The portal validates strictly and silently: any event name not in
 * STOREFRONT_EVENT_NAMES is dropped, and so is any field not in normaliseEvent.
 * That is why the surface travels as `listName` and the card index as
 * `listPosition` — both are existing, validated fields meant for exactly this —
 * and everything else goes inside `extra`, which is passed through whole.
 *
 * `listing_impression` and `listing_click` are already accepted.
 * `upsell_add_to_cart` is NOT, and events under that name will be discarded
 * until it is added to STOREFRONT_EVENT_NAMES in
 * src/lib/storefront-events.ts in the employee portal.
 */
(function () {
  'use strict';

  if (typeof window === 'undefined') return;
  if (window.mgUpsell && window.mgUpsell.__real) return;

  var SURFACE = {
    PDP: 'pdp_addons',
    CART: 'cart_addons',
    LASTCHANCE: 'lastchance'
  };

  // Half the card, for half a second. Below either threshold a card has
  // scrolled past rather than been looked at.
  var VISIBLE_RATIO = 0.5;
  var DWELL_MS = 500;

  function send(name, fields, extra, opts) {
    try {
      if (typeof window.mgTrack !== 'function') return;
      var payload = {
        listName: fields.surface || null,
        listPosition: fields.position || undefined,
        productHandle: fields.handle || null
      };
      if (extra) payload.extra = extra;
      window.mgTrack(name, payload, opts);
    } catch (e) {
      /* analytics must never break a storefront */
    }
  }

  var observer = null;
  var timers = new WeakMap();

  function ensureObserver() {
    if (observer || typeof IntersectionObserver !== 'function') return observer;

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var el = entry.target;

          if (!entry.isIntersecting || entry.intersectionRatio < VISIBLE_RATIO) {
            // Scrolled away before the dwell elapsed: not an impression.
            var pending = timers.get(el);
            if (pending) {
              clearTimeout(pending);
              timers.delete(el);
            }
            return;
          }

          if (timers.has(el)) return;

          timers.set(
            el,
            window.setTimeout(function () {
              timers.delete(el);
              if (el.dataset.mgSeen === '1') return;
              el.dataset.mgSeen = '1';
              observer.unobserve(el);

              send('listing_impression', {
                surface: el.dataset.mgSurface,
                position: Number(el.dataset.mgPosition) || undefined,
                handle: el.dataset.mgHandle
              });
            }, DWELL_MS)
          );
        });
      },
      { threshold: [VISIBLE_RATIO] }
    );

    return observer;
  }

  /**
   * Watch one card. Safe to call repeatedly on the same element — the drawer
   * re-renders its upsell row after every cart change, and a card that has
   * already counted must not count again.
   */
  function observe(el, fields) {
    try {
      if (!el || !fields || !fields.surface) return;
      if (el.dataset.mgSeen === '1') return;

      el.dataset.mgSurface = fields.surface;
      el.dataset.mgHandle = fields.handle || '';
      if (fields.position) el.dataset.mgPosition = String(fields.position);

      var io = ensureObserver();
      if (!io) {
        // No IntersectionObserver: count it rather than lose the surface
        // entirely, and mark it so the numbers can be read with that in mind.
        el.dataset.mgSeen = '1';
        send('listing_impression', fields, { observer: 'unsupported' });
        return;
      }

      io.observe(el);
    } catch (e) {}
  }

  function click(fields, extra) {
    send('listing_click', fields, extra);
  }

  /**
   * Fired only once the cart has CONFIRMED the line. An intent that failed is
   * not an add, and counting it would make every surface look better than it is.
   */
  function add(fields, extra) {
    send('upsell_add_to_cart', fields, extra);
  }

  window.mgUpsell = {
    SURFACE: SURFACE,
    observe: observe,
    click: click,
    add: add,
    __real: true
  };
})();
